import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms'

import { map } from 'rxjs'

import { FlxFormControlModule } from '@flowx/angular-ui-toolkit'
import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxIconModule,
  FlxModalModule,
} from '@flowx/angular-ui-toolkit'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import {
  ExistingResourceImportStrategy,
  ImportDecision,
  ImportResourceSessionResponse,
} from '@flowx/designer/shared/entities/existing-resource.entity'

@Component({
  selector: 'flx-existing-resources-modal',
  imports: [
    CommonModule,
    FlxModalModule,
    ReactiveFormsModule,
    FlxButtonModule,
    FlxIconModule,
    FlxSpinnerModule,
    FlxFormControlModule,
  ],
  templateUrl: './existing-resources-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './existing-resources-modal.component.scss',
})
export class FlxExistingResourcesModalComponent {
  @Output() dataEventEmitter = new EventEmitter<{
    decision: ImportDecision
    updatedImportResourceSession?: ImportResourceSessionResponse
  }>()
  existingResources: ImportResourceSessionResponse = inject(
    FLX_MODAL_DATA
  ) as ImportResourceSessionResponse
  applyToAllFC = new FormControl()
  existingResourcesFA: FormArray = new FormArray(
    this.existingResources.conflicts.map(
      (conflict) => new FormControl(conflict.importStrategy || ExistingResourceImportStrategy.Add)
    )
  )

  selectOptions = [
    {
      label: 'Keep both',
      value: ExistingResourceImportStrategy.Add,
    },
    {
      label: 'Replace',
      value: ExistingResourceImportStrategy.Replace,
    },
    {
      label: 'Skip this one',
      value: ExistingResourceImportStrategy.Keep_From_Target,
    },
  ]
  userChoiceSkippAll = toSignal(
    this.existingResourcesFA.valueChanges.pipe(
      map((values) => {
        const everyResourceIsSkipped = values.every(
          (value) => value === ExistingResourceImportStrategy.Keep_From_Target
        )
        const allResourcesHaveConflicts =
          this.existingResources.numberOfElementsToImport === values.length
        return everyResourceIsSkipped && allResourcesHaveConflicts
      })
    )
  )
  showLoader = signal(!this.existingResources.conflicts.length)
  constructor() {
    this.applyToAllFC.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      const formControlValues = Array(this.existingResourcesFA.length)
      formControlValues.fill(value)
      this.existingResourcesFA.patchValue(formControlValues)
    })
  }

  cancelImport(): void {
    this.dataEventEmitter.emit({ decision: ImportDecision.Abort })
  }
  continueImport(): void {
    this.showLoader.set(true)
    this.dataEventEmitter.emit({
      decision: ImportDecision.Resume,
      updatedImportResourceSession: {
        ...this.existingResources,
        conflicts: this.existingResources.conflicts.map((conflict, index) => ({
          ...conflict,
          importStrategy: this.existingResourcesFA.controls[index].value,
        })),
      },
    })
  }
}
